import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, RefreshCw } from "lucide-react";

import { AdminConsole } from "../components/AdminConsole";
import { ChangePasswordPage } from "../components/ChangePasswordPage";
import { LoginPage } from "../components/LoginPage";
import { getCurrentUser } from "../lib/api";

const TOKEN_STORAGE_KEY = "wamaster.access_token";

export function App() {
  const [token, setToken] = useState<string | null>(() => window.localStorage.getItem(TOKEN_STORAGE_KEY));

  const currentUser = useQuery({
    queryKey: ["auth", "me", token],
    queryFn: ({ signal }) => getCurrentUser(token ?? "", signal),
    enabled: token !== null,
    retry: false,
  });

  // Auto-recover from expired or invalid tokens by clearing localStorage
  useEffect(() => {
    if (token !== null && currentUser.isError) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      setToken(null);
    }
  }, [token, currentUser.isError]);

  useEffect(() => {
    function handleUnauthorized() {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      setToken(null);
    }
    window.addEventListener("wamaster:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("wamaster:unauthorized", handleUnauthorized);
  }, []);


  function authenticated(accessToken: string) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
    setToken(accessToken);
  }

  function logout() {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
  }

  if (token === null) {
    return <LoginPage onAuthenticated={authenticated} />;
  }

  if (currentUser.isLoading) {
    return (
      <div className="min-h-screen bg-[#07090E] flex flex-col items-center justify-center p-4">
        <div className="glass-panel p-8 max-w-sm w-full text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center font-black text-slate-950 text-xl mx-auto shadow-xl shadow-emerald-500/25 animate-pulse">
            WA
          </div>
          <div className="flex items-center justify-center gap-2 text-xs font-semibold text-emerald-400">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Verifying session security...</span>
          </div>
        </div>
      </div>
    );
  }

  if (currentUser.error || !currentUser.data) {
    return (
      <div className="min-h-screen bg-[#07090E] flex flex-col items-center justify-center p-4">
        <div className="glass-panel p-8 max-w-md w-full text-center space-y-4 border-slate-800">
          <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-400 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold text-white">Session Unavailable</h1>
          <p className="text-xs text-slate-400">
            Your access token has expired or is no longer valid. Please sign in again.
          </p>
          <button className="btn-primary w-full py-2.5 text-xs font-bold" type="button" onClick={logout}>
            Return to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (currentUser.data.must_change_password) {
    return <ChangePasswordPage token={token} user={currentUser.data} onChanged={logout} />;
  }

  return <AdminConsole token={token} user={currentUser.data} onLogout={logout} />;
}
